import mixins from 'vue-typed-mixins'
import Section from '@/calendesk/sections/section/mixins/Section'
import DefaultButton from '@/calendesk/sections/section/components/DefaultButton.vue'
import CServicesField from '@/calendesk/sections/section/booking/components/CServicesField.vue'
import { DateTimeFormats } from '@/calendesk/models/DateTimeFormats'
import ServiceSelectable from '@/calendesk/models/ServiceSelectable'
import { mapActions, mapGetters } from 'vuex'
import Employee from '@/calendesk/models/DTO/Response/Employee'
import Service from '@/calendesk/models/DTO/Response/Service'
import SectionConfigurationService from '@/calendesk/models/SectionConfigurationService'
import Dialog from '@/calendesk/models/Dialog'
import { DialogTypes } from '@/components/dialogs/DialogTypes'
import DialogSize from '@/calendesk/models/DialogSize'
import truncate from '@/calendesk/filters/truncate'
import ServiceType from '@/calendesk/models/DTO/Response/ServiceType'
import Category from '@/calendesk/models/DTO/Response/Category'
import cloneClassObject from '@/calendesk/tools/cloneClassObject'
import BasicUserSubscription from '@/calendesk/models/DTO/Response/BasicUserSubscription'

export default mixins(Section).extend({
  components: {
    DefaultButton,
    CServicesField
  },
  data () {
    return {
      firstDateSet: false,
      serviceInitialSelected: false,
      selectedEmployeeIds: [] as Array<number>,
      selectedEmployeeId: null as number | null,
      selectableEmployees: [] as Array<Record<string, string>>,
      selectedDate: this.$moment().format(DateTimeFormats.FULL) as string,
      requestedDate: null as string | null,
      minDate: new Date().toISOString().substr(0, 10),
      maxDate: null as string | null,
      selectedServiceAndType: null as ServiceSelectable | null,
      selectedServices: [] as ServiceSelectable[],
      isLoading: false,
      isFetchingTimeSlots: false,
      now: this.$moment(),
      weekday: [1, 2, 3, 4, 5, 6, 0],
      customerTimeZone: null as null | string,
      userRequestedEmployee: false,
      lockDateChange: false
    }
  },
  mounted () {
    if (!this.isTimeZoneHidden) {
      this.customerTimeZone = this.guessTimeZone()
    }

    if (this.$route.query.date) {
      const date = this.$moment(this.$route.query.date as string)

      if (date.isValid()) {
        this.selectedDate = this.$moment(this.$route.query.date as string).format(
          DateTimeFormats.FULL
        ) as string

        this.requestedDate = this.selectedDate
      }
    }

    this.checkIfShouldForceCalendarToDateRange()
  },
  computed: {
    ...mapGetters({
      services: 'service/getServices',
      closedDialogData: 'dialog/getClosedDialogData',
      shouldReload: 'booking/getReloadAllBookings',
      categoryAndServices: 'service/getCategoryAndServices'
    }),
    calendarInitialServiceSelectionEnabled (): boolean {
      return !!this.data.configuration.wb_initial_service_selection__checkbox__
    },
    calendarInitialServiceSelectionSupported (): boolean {
      return this.calendarInitialServiceSelectionEnabled &&
        (this.filteredServices.length > 1 ||
        (this.filteredServices.length === 1 && this.filteredServices[0].types && this.filteredServices[0].types.length > 0))
    },
    getTruncatedServiceDescription (): string | null {
      if (this.selectedServiceAndType && this.selectedServiceAndType.service) {
        const description = truncate(this.selectedServiceAndType.service.description as string, 150)
        return this.replaceUrlsWithLinks(description as string)
      }

      return null
    },
    serviceDurationBelowDay (): boolean {
      if (this.selectedServiceAndType) {
        return this.selectedServiceAndType.getDuration() <= 1435
      }

      return true
    },
    hideBookingEndTime (): boolean {
      return (
        this.draftConfiguration.wb_hide_booking_time_to__checkbox__ ||
        !this.serviceDurationBelowDay
      )
    },
    isToday (): boolean {
      return this.$moment(this.selectedDate, DateTimeFormats.FULL)
        .startOf('day')
        .isSame(this.$moment().startOf('day'))
    },
    serviceMaxPeople (): number {
      if (this.selectedServiceAndType && this.selectedServiceAndType.service) {
        return this.selectedServiceAndType.service.maxPeople
      }

      return 1
    },
    serviceEmployees (): Array<Employee> {
      if (
        this.selectedServiceAndType &&
        this.selectedServiceAndType.service &&
        this.selectedServiceAndType.service.employees
      ) {
        if (this.$route.query.employees) {
          const filteredEmployeeIds = String(this.$route.query.employees).split(
            ','
          )

          return this.selectedServiceAndType.service.employees.filter(
            (employee: Employee) =>
              filteredEmployeeIds.includes(String(employee.id))
          )
        }

        return this.selectedServiceAndType.service.employees
      }

      return []
    },
    filteredServicesGroupedByCategories (): Array<Category> {
      const result: Array<Category> = []
      const categories = cloneClassObject(this.categoryAndServices)

      categories.forEach((category: Category) => {
        const services = this.filteredServices.filter((service: Service) => service.categoryId === category.id)
        category.services = services
        result.push(category)
      })

      return result.filter((category: Category) => category.services && category.services.length > 0)
    },
    filteredServices (): Array<Service> {
      let services = this.services

      if (this.$route.query.employees) {
        const filteredEmployeeIds = String(this.$route.query.employees).split(
          ','
        )
        services = services.filter((service: Service) => {
          let employeeAssignedToService = false

          service.employees?.forEach((employee: Employee) => {
            if (filteredEmployeeIds.includes(String(employee.id))) {
              employeeAssignedToService = true
            }
          })

          return employeeAssignedToService
        })
      }

      const filteredServices = this.data.configuration
        .wb_service_list__services__

      let filteredServiceIds: string[] | null = null

      if (filteredServices) {
        filteredServiceIds = filteredServices.map(
          (item: SectionConfigurationService) => String(item.id)
        )
      }

      if (this.$route.query.services) {
        filteredServiceIds = String(this.$route.query.services).split(',')
      }

      if (filteredServiceIds !== null && filteredServiceIds.length > 0) {
        services = services.filter((service: Service) =>
          filteredServiceIds?.includes(String(service.id))
        )
      }

      if (
        this.data.configuration.wb_only_services_with_subscriptions__checkbox__
      ) {
        // When this option is enabled, we show services only if the user is logged in and has them in the subscriptions.
        let subscriptionServices = []

        if (
          this.user &&
          this.user.subscriptions &&
          this.user.subscriptions.length > 0
        ) {
          const allowedServiceIdsInSubscriptions: Array<number> = []
          this.user.subscriptions.forEach(
            (userSubscription: BasicUserSubscription) => {
              if (
                userSubscription.isActive() &&
                userSubscription.subscription &&
                userSubscription.subscription.serviceIds &&
                userSubscription.subscription.serviceIds.length > 0
              ) {
                userSubscription.subscription.serviceIds.forEach(
                  (id: number) => {
                    allowedServiceIdsInSubscriptions.push(id)
                  }
                )
              }
            }
          )

          subscriptionServices = services.filter((service: Service) =>
            allowedServiceIdsInSubscriptions.includes(service.id)
          )
        }

        return subscriptionServices
      }

      return services
    }
  },
  methods: {
    ...mapActions({
      fetchAvailableBookingSlots: 'booking/fetchAvailableBookingSlots',
      setReloadAllBookings: 'booking/setReloadAllBookings',
      fetchBookings: 'booking/fetchAll'
    }),
    checkIfShouldForceCalendarToDateRange () {
      const forceMinDate = this.data.configuration.wb_calendar_min_date__date__ as string | null | undefined
      const forceMaxDate = this.data.configuration.wb_calendar_max_date__date__ as string | null | undefined

      if (forceMinDate || forceMaxDate) {
        const minDate = forceMinDate ? this.$moment(forceMinDate) : this.$moment()
        const maxDate = forceMaxDate ? this.$moment(forceMaxDate) : null
        const currentDate = this.$moment()

        if (currentDate.isBefore(minDate)) {
          this.selectedDate = minDate.format(DateTimeFormats.FULL)
        } else if (maxDate && currentDate.isAfter(maxDate)) {
          this.selectedDate = maxDate.format(DateTimeFormats.FULL)
        } else {
          this.selectedDate = currentDate.format(DateTimeFormats.FULL)
        }

        this.minDate = minDate.format(DateTimeFormats.FULL)

        if (maxDate) {
          if (maxDate.isBefore(minDate)) {
            this.maxDate = minDate.format(DateTimeFormats.FULL)
          } else {
            this.maxDate = maxDate.format(DateTimeFormats.FULL)
          }
        }

        this.lockDateChange = true
      }
    },
    calendarPrev () {
      this.selectedDate = this.$moment(this.selectedDate, DateTimeFormats.FULL)
        .subtract(7, 'days')
        .format(DateTimeFormats.FULL)
    },
    calendarNext () {
      this.selectedDate = this.$moment(this.selectedDate, DateTimeFormats.FULL)
        .add(7, 'days')
        .format(DateTimeFormats.FULL)
    },
    setToday () {
      this.selectedDate = this.$moment().format(DateTimeFormats.FULL)
    },
    employeeById (employeeId: number): Employee | null {
      const employee = this.serviceEmployees.find(
        (employee: Employee) => employee.id === employeeId
      )

      if (employee) {
        return employee
      }

      return null
    },
    selectFirstServiceAsDefault () {
      if (this.filteredServices && this.filteredServices.length > 0) {
        const firstService = this.filteredServices[0]
        let serviceType = null
        if (firstService.types && firstService.types.length > 0) {
          serviceType = firstService.types[0]
        }

        this.selectedServiceAndType = new ServiceSelectable(
          firstService,
          serviceType
        )
      }
    },
    selectAllEmployees () {
      this.selectedEmployeeIds = this.serviceEmployees.map((employee: Employee) => {
        return employee.id
      })
    },
    openDescriptionDialog (service: Service, serviceType: ServiceType|null) {
      let title = service.name

      if (serviceType) {
        title += `(${serviceType.name})`
      }

      this.openDialog(
        new Dialog(
          true,
          DialogTypes.BOOKING_SERVICE_DESCRIPTION_DIALOG,
          title,
          DialogSize.SMALL,
          false,
          'center',
          {
            description: service.description
          }
        )
      )
    },
    initCalendarWithService (selectedServiceAndType: ServiceSelectable) {
      if (this.serviceEmployees.length === 1) {
        this.selectedEmployeeId = null
      }

      this.serviceInitialSelected = true
      this.selectedServiceAndType = selectedServiceAndType

      this.$nextTick(() => {
        if (this.selectedServiceAndType) {
          const element = document.querySelector('.flexible-booking-calendar')
          if (element) {
            element.scrollIntoView({ behavior: 'auto', block: 'center' })
          }
        }
      })
    },
    goToInitCalendarPage () {
      this.serviceInitialSelected = false
      this.firstDateSet = false

      this.$nextTick(() => {
        if (this.selectedServiceAndType) {
          const element = document.querySelector(`#id_service_${this.selectedServiceAndType.service.id}`)
          if (element) {
            element.scrollIntoView({ behavior: 'auto', block: 'center' })
          }
        }
      })
    }
  }
})
